import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { useMapsLibLoaded } from '../../util/mapsLib';
import SearchMapContent from './SearchMapContent';

const MapChannelContext = createContext(null);

// Offscreen-hidden parking — keeps the node sized (Mapbox needs non-zero dims),
// so it is NOT display:none. Mirrors the former `reusableMapHidden` rule.
const PARKED_STYLE = {
  position: 'absolute',
  top: '-1000px',
  left: '-1000px',
  width: '50vw',
  height: '50vh',
  visibility: 'hidden',
  opacity: '0',
  pointerEvents: 'none',
};

const ACTIVE_STYLE = {
  position: '',
  top: '',
  left: '',
  width: '100%',
  height: '100%',
  visibility: '',
  opacity: '',
  pointerEvents: '',
};

const shallowEqualProps = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a[k] === b[k]);
};

export const useMapChannel = () => {
  const ctx = useContext(MapChannelContext);
  if (!ctx) throw new Error('useMapChannel must be used within <MapProvider>');
  return ctx;
};

const MapProvider = ({ children }) => {
  // Reactive: mapbox-gl is a deferred script, so hydration can run before it
  // exists — this flips to true (and renders the portals) once it has loaded.
  const mapsLibLoaded = useMapsLibLoaded();
  const nodesRef = useRef({}); // { [channel]: HTMLDivElement }
  const holderRef = useRef(null); // hidden holder for parked nodes
  const activeSlotsRef = useRef({}); // { [channel]: slotEl }
  // { [channel]: { props, isActive, activationToken } } — only claimed channels appear (lazy)
  const [channels, setChannels] = useState({});

  const ensureNode = useCallback((channel) => {
    if (typeof document === 'undefined') return null;
    if (!nodesRef.current[channel]) {
      const node = document.createElement('div');
      node.id = `map-channel-${channel}`;
      Object.assign(node.style, ACTIVE_STYLE);
      nodesRef.current[channel] = node;
    }
    return nodesRef.current[channel];
  }, []);

  const claim = useCallback(
    (channel, slotEl, props) => {
      if (
        process.env.NODE_ENV !== 'production' &&
        activeSlotsRef.current[channel] &&
        activeSlotsRef.current[channel] !== slotEl &&
        activeSlotsRef.current[channel].isConnected
      ) {
        console.warn(
          `MapProvider: channel "${channel}" claimed by a second slot while still active in another. ` +
            'The single shared map node can only live in one slot at a time.'
        );
      }
      activeSlotsRef.current[channel] = slotEl;
      const node = ensureNode(channel);
      if (!node || !slotEl) return;
      Object.assign(node.style, ACTIVE_STYLE);
      slotEl.appendChild(node);
      setChannels((s) => ({
        ...s,
        [channel]: {
          props,
          isActive: true,
          activationToken: (s[channel]?.activationToken || 0) + 1,
        },
      }));
    },
    [ensureNode]
  );

  const update = useCallback((channel, props) => {
    setChannels((s) => {
      const cur = s[channel];
      if (!cur || shallowEqualProps(cur.props, props)) return s;
      return { ...s, [channel]: { ...cur, props } };
    });
  }, []);

  const release = useCallback((channel) => {
    if (activeSlotsRef.current[channel]) activeSlotsRef.current[channel] = null;
    const node = nodesRef.current[channel];
    if (node && holderRef.current) {
      Object.assign(node.style, PARKED_STYLE);
      holderRef.current.appendChild(node);
    }
    setChannels((s) => (s[channel] ? { ...s, [channel]: { ...s[channel], isActive: false } } : s));
  }, []);

  const ctxValue = useMemo(() => ({ claim, update, release }), [claim, update, release]);

  return (
    <MapChannelContext.Provider value={ctxValue}>
      {children}
      <div
        aria-hidden
        ref={holderRef}
        style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
      />
      {mapsLibLoaded
        ? Object.entries(channels).map(([channel, st]) => {
            const node = nodesRef.current[channel];
            if (!node) return null;
            return ReactDOM.createPortal(
              <SearchMapContent
                {...st.props}
                activationToken={st.activationToken}
                isActive={st.isActive}
              />,
              node,
              channel
            );
          })
        : null}
    </MapChannelContext.Provider>
  );
};

export default MapProvider;
